import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FAQSidebar } from "@/components/faq/FAQSidebar";
import { FAQSearchBar } from "@/components/faq/FAQSearchBar";
import { FAQAnswerCard } from "@/components/faq/FAQAnswerCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { allCategories, faqQuestions } from "@/data/nis2-faq-data";
import { FAQSearchFilters, SortOption } from "@/types/faq";

const NIS2FAQBrowse = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialCategory = location.state?.category || "all";

  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>();
  const [searchFilters, setSearchFilters] = useState<FAQSearchFilters>({});
  const [sortBy, setSortBy] = useState<SortOption>("frecvente");

  useEffect(() => {
    if (initialCategory !== "all") {
      setSelectedCategory(initialCategory);
    }
  }, [initialCategory]);

  const filteredQuestions = useMemo(() => {
    let filtered = [...faqQuestions];

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter((q) => q.category === selectedCategory);
    }

    // Filter by subcategory
    if (selectedSubcategory) {
      filtered = filtered.filter((q) => q.subcategory === selectedSubcategory);
    }

    // Filter by search keyword
    if (searchFilters.keyword) {
      const keyword = searchFilters.keyword.toLowerCase();
      filtered = filtered.filter(
        (q) =>
          q.question.toLowerCase().includes(keyword) ||
          q.answer.toLowerCase().includes(keyword) ||
          q.tags.some((tag) => tag.toLowerCase().includes(keyword))
      );
    }

    // Filter by entity type
    if (searchFilters.entityType && searchFilters.entityType !== "all") {
      const entityTag =
        searchFilters.entityType === "esential"
          ? "Entități esențiale"
          : "Entități importante";
      filtered = filtered.filter((q) => q.tags.includes(entityTag));
    }

    // Filter by urgency
    if (searchFilters.urgency && searchFilters.urgency !== "all") {
      if (searchFilters.urgency === "high") {
        filtered = filtered.filter((q) => q.urgent);
      }
    }

    // Sort
    switch (sortBy) {
      case "frecvente":
        filtered.sort((a, b) => b.votes - a.votes);
        break;
      case "recente":
        filtered.sort(
          (a, b) =>
            new Date(b.lastUpdated).getTime() -
            new Date(a.lastUpdated).getTime()
        );
        break;
      case "alfabetic":
        filtered.sort((a, b) => a.question.localeCompare(b.question, "ro"));
        break;
    }

    return filtered;
  }, [selectedCategory, selectedSubcategory, searchFilters, sortBy]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/nis2-faq")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Înapoi
            </Button>
            <h1 className="text-2xl font-bold text-foreground">
              Întrebări și răspunsuri NIS2
            </h1>
          </div>

          <FAQSearchBar onSearch={setSearchFilters} />
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-32">
              <FAQSidebar
                categories={allCategories}
                selectedCategory={selectedCategory}
                selectedSubcategory={selectedSubcategory}
                onCategorySelect={(id) => {
                  setSelectedCategory(id);
                  setSelectedSubcategory(undefined);
                }}
                onSubcategorySelect={(catId, subId) => {
                  setSelectedCategory(catId);
                  setSelectedSubcategory(subId);
                }}
              />
            </div>
          </aside>

          {/* Questions List */}
          <div className="space-y-6">
            {/* Results Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {filteredQuestions.length} întrebări și răspunsuri
                </h2>
                {selectedSubcategory && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {
                      allCategories
                        .find((c) => c.id === selectedCategory)
                        ?.subcategories?.find(
                          (s) => s.id === selectedSubcategory
                        )?.title
                    }
                  </p>
                )}
              </div>

              <Select
                value={sortBy}
                onValueChange={(value) => setSortBy(value as SortOption)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="frecvente">Cele mai frecvente</SelectItem>
                  <SelectItem value="recente">Cele mai recente</SelectItem>
                  <SelectItem value="alfabetic">Alfabetic</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Questions Grid */}
            {filteredQuestions.length > 0 ? (
              <div className="grid gap-4">
                {filteredQuestions.map((question) => (
                  <FAQAnswerCard
                    key={question.id}
                    question={question}
                    onClick={() => navigate(`/nis2-faq/answer/${question.id}`)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  Nu am găsit întrebări care să corespundă criteriilor tale.
                </p>
                <Button
                  variant="link"
                  onClick={() => {
                    setSelectedCategory("all");
                    setSelectedSubcategory(undefined);
                    setSearchFilters({});
                  }}
                  className="mt-4"
                >
                  Resetează filtrele
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default NIS2FAQBrowse;
